/**
 * ErrorBoundary — app-wide crash-safety net. Real tender data is where
 * unexpected shapes appear; without this, a single render throw blanks the
 * whole SPA. Shows an Italian fallback with a reload action instead.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** When this changes (e.g. route pathname), a shown error is cleared so
   *  Back/Forward navigation escapes the fallback instead of freezing on it. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in the console for debugging; no telemetry backend yet.
    console.error("Errore non gestito nell'interfaccia:", error, info.componentStack);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "var(--c-bg)",
        }}
      >
        <div
          className="card"
          style={{
            maxWidth: 440,
            padding: "32px 30px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--c-ink)" }}>
            Si è verificato un errore imprevisto
          </h2>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--c-ink-2)" }}>
            I tuoi dati sono al sicuro: si tratta di un problema di visualizzazione. Ricarica la
            pagina per riprendere da dove eri.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
              Ricarica la pagina
            </button>
            <button className="btn btn-sm" onClick={() => window.location.assign("/")}>
              Torna al Workspace
            </button>
          </div>
          <p
            className="t-mono"
            style={{ margin: 0, fontSize: 10.5, color: "var(--c-ink-3)", wordBreak: "break-word" }}
          >
            {this.state.error.message}
          </p>
        </div>
      </div>
    );
  }
}
